/**
 * Notification Center - overview + campaign list.
 *
 * The headline question this page has to answer is not "how many did we send"
 * but "did any of it make money" (§35), so revenue and orders sit in the tile
 * row next to reach, and the campaign table sorts by revenue rather than by
 * recency.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Megaphone, Plus, RefreshCw } from 'lucide-react'
import { useColors } from '../../hooks/useColors'
import EmptyState from '../../components/shared/EmptyState'
import {
  Card, CardTitle, Chip, PageHeader, PillButton, SearchInput,
  SegmentedControl, SelectInput, TableHead, Td,
} from '../../components/shared/primitives'
import '../../styles/notifications.css'
import { notificationsApi } from '../../features/notifications/api'
import { STATUS_LABEL, STATUS_TONE, SUPPRESSION_LABEL } from '../../features/notifications/constants'
import type { Campaign, CampaignPerformance, Overview, WhatsAppAnalytics } from '../../features/notifications/types'

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
] as const

const money = (value: number) => `₹${Math.round(value).toLocaleString('en-IN')}`
const pct = (value: number) => `${value.toFixed(1)}%`

/**
 * One figure in the strip. Revenue gets the accent because it is the only number
 * on this page that answers "did this work" - everything else is an input to it.
 */
function Stat({ label, value, hint, accent }: {
  label: string
  value: string
  hint?: string
  accent?: boolean
}) {
  return (
    <div className="ntf-stat">
      <div className="ntf-stat__label">{label}</div>
      <div
        className="ntf-stat__value font-display"
        style={accent ? { color: 'var(--accent-text)' } : undefined}
      >
        {value}
      </div>
      {hint ? <div className="ntf-stat__hint">{hint}</div> : null}
    </div>
  )
}

export default function NotificationCenterPage() {
  const colors = useColors()
  const navigate = useNavigate()

  const [range, setRange] = useState<'today' | '7d' | '30d'>('today')
  const [overview, setOverview] = useState<Overview | null>(null)
  const [whatsapp, setWhatsapp] = useState<WhatsAppAnalytics | null>(null)
  const [performance, setPerformance] = useState<CampaignPerformance[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [overviewData, performanceData, campaignData, whatsappData] = await Promise.all([
        notificationsApi.getOverview(range),
        notificationsApi.getCampaignPerformance(range === 'today' ? '7d' : range),
        notificationsApi.listCampaigns({ status: statusFilter || undefined, q: search || undefined, limit: 50 }),
        // WhatsApp delivery is a separate channel; a failure here must never blank
        // the whole page, so it degrades to null on its own.
        notificationsApi.getWhatsAppAnalytics().catch(() => null),
      ])
      setOverview(overviewData)
      setPerformance(performanceData)
      setCampaigns(campaignData.campaigns)
      setWhatsapp(whatsappData)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not load the Notification Center'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [range, statusFilter, search])

  useEffect(() => {
    void load()
  }, [load])

  const totals = overview?.totals
  const revenueByCampaign = useMemo(
    () => new Map(performance.map((row) => [row.id, row])),
    [performance]
  )

  return (
    <div>
      <PageHeader
        title="Notification Center"
        subtitle="Campaigns, engagement content and what each one actually earned."
        actions={(
          <>
            <SegmentedControl
              value={range}
              options={RANGES.map((item) => ({ value: item.value, label: item.label }))}
              onChange={(value) => setRange(value)}
              size="sm"
            />
            <PillButton variant="secondary" onClick={() => void load()} title="Refresh">
              <RefreshCw size={14} /> Refresh
            </PillButton>
            <PillButton onClick={() => navigate('/notifications/new')}>
              <Plus size={14} /> New campaign
            </PillButton>
          </>
        )}
      />

      {error ? (
        <Card style={{ marginBottom: '18px', borderColor: '#B45309' }}>
          <span style={{ color: '#B45309' }}>{error}</span>
        </Card>
      ) : null}

      {/* One bordered strip of figures. Six separately-shadowed cards read as six
          unrelated widgets; this reads as one report, which is what it is. */}
      <div className="ntf-stats">
        <Stat label="Sent" value={(totals?.sent ?? 0).toLocaleString('en-IN')} hint={`${(totals?.targeted ?? 0).toLocaleString('en-IN')} targeted`} />
        <Stat
          label="Delivered"
          value={(totals?.delivered || totals?.sent || 0).toLocaleString('en-IN')}
          hint={totals?.denominator === 'sent' ? 'no receipts, using sent' : 'device receipts'}
        />
        <Stat label="Opened" value={(totals?.opened ?? 0).toLocaleString('en-IN')} />
        <Stat label="Open rate" value={pct(totals?.open_rate ?? 0)} />
        <Stat label="Orders" value={(totals?.converted ?? 0).toLocaleString('en-IN')} />
        <Stat
          label="Revenue"
          value={money(totals?.revenue ?? 0)}
          hint={`${money(totals?.revenue_per_notification ?? 0)} per notification`}
          accent
        />
      </div>

      {/* WhatsApp marketing delivery, classified. Only shown once a WhatsApp
          campaign has actually run (attempted > 0), so it never adds noise to an
          account that only sends push. It answers "why did N fail" with an
          action per category, never a single "failed" number. */}
      {whatsapp && whatsapp.attempted > 0 ? (
        <Card style={{ marginTop: '18px' }}>
          <CardTitle>WhatsApp delivery</CardTitle>
          <div className="ntf-stats" style={{ marginTop: '10px' }}>
            <Stat label="Attempted" value={whatsapp.attempted.toLocaleString('en-IN')} />
            <Stat
              label="Delivered"
              value={whatsapp.delivered.toLocaleString('en-IN')}
              hint={pct(whatsapp.delivered_pct)}
              accent
            />
          </div>
          <div style={{ marginTop: '12px' }}>
            {whatsapp.categories.length === 0 ? (
              <div className="ntf-stat__hint">No failures. Everything delivered.</div>
            ) : (
              whatsapp.categories
                .filter((c) => c.category !== 'DELIVERED')
                .map((c) => (
                  <div key={c.category} className="ntf-row" title={c.action}>
                    <span>
                      {c.label}
                      <span className="ntf-stat__hint" style={{ marginLeft: '8px' }}>
                        {c.retryable ? 'retry after cooldown' : 'do not retry'} · {c.action}
                      </span>
                    </span>
                    <span className="ntf-row__value">
                      {c.count.toLocaleString('en-IN')} ({pct(c.pct)})
                    </span>
                  </div>
                ))
            )}
          </div>
        </Card>
      ) : null}

      <div className="ntf-split ntf-split--wide" style={{ marginTop: '18px' }}>
        <Card padded={false}>
          <div style={{ padding: '18px 20px 0' }}>
            <CardTitle
              right={(
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <SearchInput value={search} onChange={setSearch} placeholder="Search campaigns" width="180px" />
                  <SelectInput
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    style={{ width: '150px', padding: '8px 10px' }}
                    aria-label="Filter by status"
                  >
                    <option value="">All statuses</option>
                    {Object.entries(STATUS_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </SelectInput>
                </div>
              )}
            >
              Campaigns
            </CardTitle>
          </div>

          {loading && campaigns.length === 0 ? (
            <div style={{ padding: '32px', color: colors.textMuted }}>Loading…</div>
          ) : campaigns.length === 0 ? (
            <div style={{ padding: '18px' }}>
              <EmptyState
                icon={Megaphone}
                title="No campaigns yet"
                description="Create one from the content library or write it from scratch."
              />
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <TableHead
                  columns={[
                    { key: 'name', label: 'Campaign' },
                    { key: 'status', label: 'Status' },
                    { key: 'audience', label: 'Audience', align: 'right' },
                    { key: 'sent', label: 'Sent', align: 'right' },
                    { key: 'open', label: 'Open rate', align: 'right' },
                    { key: 'orders', label: 'Orders', align: 'right' },
                    { key: 'revenue', label: 'Revenue', align: 'right' },
                  ]}
                />
                <tbody>
                  {campaigns.map((campaign) => {
                    const perf = revenueByCampaign.get(campaign.id)
                    return (
                      <tr
                        key={campaign.id}
                        onClick={() => navigate(`/notifications/${campaign.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Td>
                          <div style={{ fontWeight: 600 }}>{campaign.name}</div>
                          <div style={{ fontSize: '12px', color: colors.textMuted }}>
                            {campaign.category} · {campaign.title}
                          </div>
                        </Td>
                        <Td>
                          <Chip tone={STATUS_TONE[campaign.status]}>{STATUS_LABEL[campaign.status]}</Chip>
                        </Td>
                        <Td align="right">{(campaign.audience_size ?? 0).toLocaleString('en-IN')}</Td>
                        <Td align="right">{(perf?.sent ?? campaign.stats?.sent ?? 0).toLocaleString('en-IN')}</Td>
                        <Td align="right">{perf ? pct(perf.open_rate) : '-'}</Td>
                        <Td align="right">{(perf?.converted ?? 0).toLocaleString('en-IN')}</Td>
                        <Td align="right">{money(perf?.revenue ?? 0)}</Td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div style={{ display: 'grid', gap: '18px' }}>
          <Card>
            <CardTitle>Who we did not message</CardTitle>
            <p style={{ margin: '0 0 12px', fontSize: '12px', color: colors.textMuted }}>
              Every recipient deliberately skipped, and why. A large "daily cap" number means
              campaigns are competing with each other, not that the engine is broken.
            </p>
            {(overview?.suppression ?? []).length === 0 ? (
              <div style={{ color: colors.textMuted, fontSize: '13px' }}>Nothing suppressed in this window.</div>
            ) : (
              <div>
                {overview?.suppression.map((row) => (
                  <div key={row.reason} className="ntf-row">
                    <span style={{ color: colors.textSecondary }}>
                      {SUPPRESSION_LABEL[row.reason] ?? row.reason}
                    </span>
                    <span className="ntf-row__value">{row.count.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardTitle>By category</CardTitle>
            {(overview?.by_category ?? []).length === 0 ? (
              <div style={{ color: colors.textMuted, fontSize: '13px' }}>No sends in this window.</div>
            ) : (
              <div>
                {overview?.by_category.map((row) => (
                  <div key={row.category} className="ntf-row">
                    <span style={{ color: colors.textSecondary }}>{row.category}</span>
                    <span style={{ display: 'flex', gap: '12px', alignItems: 'baseline' }}>
                      <span style={{ color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>{pct(row.open_rate)}</span>
                      <span className="ntf-row__value">{money(row.revenue)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardTitle>Content library</CardTitle>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: colors.textMuted }}>
              Reusable memes, offers and seasonal copy. Start a campaign from one that already works.
            </p>
            <PillButton variant="secondary" fullWidth onClick={() => navigate('/notifications/templates')}>
              Open library
            </PillButton>
          </Card>

          <Card>
            <CardTitle>Sending rules</CardTitle>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: colors.textMuted }}>
              How often one customer can be sent a campaign. Set a cap to 0 to remove it.
            </p>
            <PillButton variant="secondary" fullWidth onClick={() => navigate('/notifications/settings')}>
              Edit rules
            </PillButton>
          </Card>
        </div>
      </div>
    </div>
  )
}
